import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemMultiPicklistValue } from '@/common/api/types/items';
import { usePicklistOptionsQuery } from '@/common/queries/useGetCustomFieldsQuery';
import { associateById } from '@/common/utils/associateById';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import { MultiValueFieldManager, type MultiValueFieldValue } from './MultiValueFieldManager';
import { PicklistSelectionDialog } from './PicklistSelectionDialog';
import type { PicklistOption } from './types';
import { MULTI_VALUE_MAX_COUNT } from './validation';

interface MultiPicklistFieldProps {
  id: string;
  name: string;
  values?: ItemMultiPicklistValue[];
  isEditable?: boolean;
  autoFocus?: boolean;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  onChange: (selectedIds: number[]) => void;
  onValidationChange?: (error: string | null) => void;
}

export const MultiPicklistField = ({
  id,
  name,
  values = [],
  isEditable = false,
  autoFocus = false,
  isPending = false,
  isSuccess = false,
  isError,
  error,
  onChange,
  onValidationChange,
  options: optionsProp,
}: MultiPicklistFieldProps & { options?: PicklistOption[] | null }) => {
  const { t } = useTranslation();
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(autoFocus && values.length === 0);

  const { data: fetchedOptions = [] } = usePicklistOptionsQuery({
    customFieldId: Number(id),
    enabled: !optionsProp,
  });
  const picklistOptions = optionsProp ?? fetchedOptions;

  const picklistOptionsById = associateById(picklistOptions);

  const selectedIds = React.useMemo(() => values.map((v) => v.MULTI_PICKLIST), [values]);

  const handleValidationError = (err: string | null) => {
    setValidationError(err);
    onValidationChange?.(err);
  };

  const handleOnAdd = (optionId: number) => {
    if (selectedIds.includes(optionId)) return;
    if (values.length >= MULTI_VALUE_MAX_COUNT) {
      handleValidationError(
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiPicklistMaxCount, {
          maxCount: MULTI_VALUE_MAX_COUNT,
        }),
      );
      return;
    }
    handleValidationError(null);
    onChange([...selectedIds, optionId]);
  };

  const fieldValues: MultiValueFieldValue<ItemMultiPicklistValue>[] = values.map((v) => ({
    id: v.id,
    value: v,
  }));

  const resolvedIsError = !!validationError || !!isError;
  const resolvedError = validationError ? new Error(validationError) : (error ?? null);

  return (
    <FieldWithStatusIndicator name={name} isPending={isPending} isSuccess={isSuccess}>
      <div className="flex flex-col gap-1">
        <MultiValueFieldManager
          values={fieldValues}
          isEditable={isEditable}
          disabled={isPending}
          onRemove={(removedId) =>
            onChange(values.filter((v) => v.id !== removedId).map((v) => v.MULTI_PICKLIST))
          }
          maxCount={MULTI_VALUE_MAX_COUNT}
          onAddClick={() => setDialogOpen(true)}
        >
          {(item) => {
            const optionName = picklistOptionsById[item.value.MULTI_PICKLIST]?.name;
            return optionName ? <span className="text-sm">{optionName}</span> : null;
          }}
        </MultiValueFieldManager>
        {resolvedIsError && resolvedError && (
          <span className="text-xs text-text-error" role="alert">
            {resolvedError.message}
          </span>
        )}
      </div>

      <PicklistSelectionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        options={picklistOptions}
        excludedOptionIds={selectedIds}
        onSave={handleOnAdd}
        title={t(($) => $.adaptivePlanner.itemPanel.customFields.multiValue.addValue)}
      />
    </FieldWithStatusIndicator>
  );
};
